"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ProductFiltersComponent } from "@/components/shop/ProductFilters";
import { ProductGrid } from "@/components/shop/ProductGrid";
import { TonightBanner } from "@/components/food/TonightBanner";
import { ReserveTableButton } from "@/components/food/ReserveTableButton";
import { ShopControlsMinimal } from "@/components/shop/ShopControlsMinimal";
import { ProductFilters, FilterOptions, ShopProduct, ProductPagination } from "@/lib/types/shop";
import { useShopStore } from "@/lib/store/shopStore";
import { fetchQuery } from "convex/nextjs";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import { industryShortLabel } from "@/lib/config/industryForms";
import {
  FILTER_COLORS, FILTER_OCCASION_OPTIONS, FILTER_MATERIAL_OPTIONS,
  FILTER_STYLE_OPTIONS, FILTER_FITTING_OPTIONS, getSizesForSystem,
  DEFAULT_SIZE_SYSTEM, getAvailableSizeSystems,
} from "@/components/shop/product-constants";
import { toShopProduct } from "@/lib/shop/toShopProduct";
import { loadMenuStock } from "@/lib/shop/menuStock";
import { sameCondition } from "@/lib/config/productCondition";
import {
  childNamesOf,
  chipNameFor,
  linkValueFor,
  optionsFromSlugMap,
  resolveCategoryLink,
} from "@/lib/shop/categoryLinks";

interface ShopClientProps {
  initialProducts: ShopProduct[];
  pagination: ProductPagination;
  categories: string[];
  filterOptions: any; // Dynamic filter options from Convex
  subcategories?: string[];
  initialFilters: ProductFilters;
  brandSlug: string;
  brandName: string;
  brandId?: string; // Convex brand ID for Load More queries
  industry: "fashion" | "electronics";
  stores?: any[];
  /** Shown above the products while nothing is searched or filtered (the collections strip). */
  beforeGrid?: React.ReactNode;
}

export function ShopClient({ initialProducts, pagination, categories, filterOptions: initialFilterOptions, subcategories = [], initialFilters, brandSlug, brandId, industry, stores = [], beforeGrid }: ShopClientProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Get persisted state from store
  const { getShopState, setShopProducts, appendProducts, setScrollPosition } = useShopStore();
  const shopKey = `brand-${brandSlug}`;
  const persistedState = getShopState(shopKey);

  // Product accumulation state - restore from store if available
  const [allProducts, setAllProducts] = useState<ShopProduct[]>(
    persistedState?.products || initialProducts
  );
  const [currentPage, setCurrentPage] = useState(
    persistedState?.page || pagination.page
  );
  const [currentPagination, setCurrentPagination] = useState<ProductPagination>(
    persistedState?.pagination || pagination
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasRestoredScroll, setHasRestoredScroll] = useState(false);

  const [searchQuery, setSearchQuery] = useState(initialFilters.search || "");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localFilters, setLocalFilters] = useState<ProductFilters>(initialFilters);
  const [currentSizeSystem, setCurrentSizeSystem] = useState(DEFAULT_SIZE_SYSTEM);
  // The shop's whole taxonomy as the page first got it: the filter options are
  // never narrowed by a category in the address, so these are every
  // category, subcategory and product type on the website (or the branch).
  // A category link is read against them (lib/shop/categoryLinks.ts).
  const [shopTaxonomy] = useState(() => ({
    categories: (initialFilterOptions.categories || []) as any[],
    subcategories: (initialFilterOptions.subcategories || []) as any[],
    productTypes: (initialFilterOptions.productTypes || []) as any[],
  }));
  // The category link the page opened on, if any. Its lists start the way
  // they look after tapping that category and subcategory. With no link, they
  // are the full lists, as before.
  const [openedLink] = useState(() =>
    resolveCategoryLink(
      { category: initialFilters.category, subcategory: initialFilters.subcategory, productType: initialFilters.productType },
      shopTaxonomy,
    ),
  );
  const [currentSubcategories, setCurrentSubcategories] = useState<string[]>(() => {
    if (subcategories.length > 0) return subcategories;
    const inCategory = childNamesOf(openedLink.category, shopTaxonomy.subcategories, "categoryId");
    if (inCategory.length > 0) return ["All", ...inCategory];
    return ["All", ...(initialFilterOptions.subcategories?.map((s: any) => s.name) || [])];
  });
  const [currentProductTypes, setCurrentProductTypes] = useState<string[]>(() => {
    const inSubcategory = childNamesOf(openedLink.subcategory, shopTaxonomy.productTypes, "subcategoryId");
    if (inSubcategory.length > 0) return ["All", ...inSubcategory];
    return ["All", ...(initialFilterOptions.productTypes?.map((pt: any) => pt.name) || [])];
  });

  // name→slug maps so cascade queries always receive a proper slug
  // even though filterOptions stores display names for the UI
  const categorySlugMap = useRef<Record<string, string>>(
    Object.fromEntries(
      (initialFilterOptions.categories || []).map((c: any) => [c.name, c.slug])
    )
  );
  // These are refs so refetchProducts/handleLoadMore can always read the latest
  // value without needing them in the useCallback dependency array
  const subcategorySlugMapRef = useRef<Record<string, string>>(
    Object.fromEntries(
      (initialFilterOptions.subcategories || []).map((s: any) => [s.name, s.slug])
    )
  );
  const productTypeSlugMapRef = useRef<Record<string, string>>(
    Object.fromEntries(
      (initialFilterOptions.productTypes || []).map((pt: any) => [pt.name, pt.slug])
    )
  );
  // State version of subcategorySlugMap — used in handleFilterChange closure
  const [subcategorySlugMap, setSubcategorySlugMap] = useState<Record<string, string>>(
    Object.fromEntries(
      (initialFilterOptions.subcategories || []).map((s: any) => [s.name, s.slug])
    )
  );
  // Full initial product-type list — used as fallback when cascade returns empty
  const initialProductTypeNames = useRef<string[]>(
    ["All", ...(initialFilterOptions.productTypes?.map((pt: any) => pt.name) || [])]
  );
  // The whole shop's name→slug maps as on the first paint: the fallback when a
  // cascade's own map does not (yet) hold the chip someone picked, so the
  // address still gets the slug.
  const [shopSlugMaps] = useState(() => ({
    subcategory: Object.fromEntries(shopTaxonomy.subcategories.map((s: any) => [s.name, s.slug])) as Record<string, string>,
    productType: Object.fromEntries(shopTaxonomy.productTypes.map((pt: any) => [pt.name, pt.slug])) as Record<string, string>,
  }));
  // Guard: only apply SSR initialProducts on the very first mount.
  // After that, all product updates come from client-side refetchProducts.
  // Without this guard, every URL change triggers an SSR re-run whose
  // initialProducts (page 1 only) would reset the grid and its loaded pages.
  const hasAppliedInitialProducts = useRef(false);

  // Helper: translate a display name to a slug for Convex queries
  const toSlug = useCallback(
    (name: string | undefined, map: Record<string, string>): string | undefined => {
      if (!name || name === "All") return undefined;
      return map[name] || name.toLowerCase().replace(/\s+/g, "-");
    },
    []
  );

  // Categories grouped by industry — only when the catalogue spans two or
  // more (a hotel with a menu and a gift shop); a single-industry shop keeps
  // the flat list.
  const categoryGroups = (() => {
    const byIndustry = new Map<string, string[]>();
    for (const c of (initialFilterOptions.categories || []) as any[]) {
      const key = String(c.industry || industry);
      byIndustry.set(key, [...(byIndustry.get(key) || []), c.name]);
    }
    if (byIndustry.size < 2) return undefined;
    return Array.from(byIndustry.entries()).map(([slug, names]) => ({ label: industryShortLabel(slug), categories: names }));
  })();

  // Use dynamic filter options — subcategories and productTypes are driven by local state
  // so they cascade correctly when the user selects a category/subcategory
  const filterOptions: FilterOptions = {
    categories: ["All", ...(initialFilterOptions.categories?.map((c: any) => c.name) || categories.filter((c) => c !== "All"))],
    categoryGroups,
    subcategories: currentSubcategories,
    productTypes: currentProductTypes,
    brands: [], // No brand filter on brand page
    styles: [], // Not in schema - hide
    colors: initialFilterOptions.colors || [],
    // New / used (Bagz Phones, Oct 2026) — shown when the shop sells two or more.
    conditions: initialFilterOptions.conditions || [],
    sizes: initialFilterOptions.sizes || getSizesForSystem(currentSizeSystem),
    occasions: [], // Not in schema - hide
    materials: [], // Not in schema - hide
    fittings: [], // Not in schema - hide
    sizeSystem: currentSizeSystem,
    availableSizeSystems: getAvailableSizeSystems(),
    priceRange: initialFilterOptions.priceRange || { min: 0, max: 1000000 },
  };

  // Apply SSR data ONLY on the very first mount — never on subsequent filter changes.
  // Client-side refetchProducts handles all filter-driven product updates.
  // router.refresh() has been intentionally removed: it caused SSR to re-run with
  // name-based filter params (e.g. subcategory="Dresses"), which Convex couldn't
  // match by slug, returning all products and overwriting the correctly filtered state.
  useEffect(() => {
    if (hasAppliedInitialProducts.current) return; // already done — skip reset
    hasAppliedInitialProducts.current = true;

    const hasFilters = Object.keys(initialFilters).some(
      k => k !== 'sortBy' && k !== 'page' && initialFilters[k as keyof ProductFilters]
    );

    if (hasFilters || !persistedState) {
      setAllProducts(initialProducts);
      setCurrentPage(pagination.page);
      setCurrentPagination(pagination);
      setShopProducts(shopKey, initialProducts, pagination.page, pagination);
    }
  }, [initialFilters]);

  // Restore scroll position after products are rendered
  useEffect(() => {
    if (persistedState?.scrollPosition && !hasRestoredScroll && allProducts.length > 0) {
      setTimeout(() => {
        window.scrollTo(0, persistedState.scrollPosition);
        setHasRestoredScroll(true);
      }, 100);
    }
  }, [persistedState?.scrollPosition, hasRestoredScroll, allProducts.length]);

  // Save scroll position before navigating away
  useEffect(() => {
    const handleBeforeUnload = () => {
      setScrollPosition(shopKey, window.scrollY);
    };

    // Save on scroll (debounced)
    let scrollTimeout: NodeJS.Timeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        setScrollPosition(shopKey, window.scrollY);
      }, 100);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
      // Save scroll position when component unmounts (navigation)
      setScrollPosition(shopKey, window.scrollY);
    };
  }, [shopKey, setScrollPosition]);

  const getFiltersFromParams = useCallback(() => {
    const params = Object.fromEntries(searchParams.entries());
    const sizesParam = params.sizes;
    // The address names a category by its slug (older links: by the chip's
    // name); the chips go by name. Read against the whole shop plus whatever
    // the cascades fetched since (lib/shop/categoryLinks.ts). A value that
    // matches nothing stays as it is, as before.
    const rawLink = {
      category: params.category || undefined,
      subcategory: params.subcategory || undefined,
      productType: params.productType || undefined,
    };
    const link = resolveCategoryLink(rawLink, {
      categories: [...shopTaxonomy.categories, ...optionsFromSlugMap(categorySlugMap.current)],
      subcategories: [...shopTaxonomy.subcategories, ...optionsFromSlugMap(subcategorySlugMapRef.current)],
      productTypes: [...shopTaxonomy.productTypes, ...optionsFromSlugMap(productTypeSlugMapRef.current)],
    });
    return {
      category: chipNameFor(link.category, rawLink.category),
      subcategory: chipNameFor(link.subcategory, rawLink.subcategory),
      productType: chipNameFor(link.productType, rawLink.productType),
      store: params.store || undefined,
      minPrice: params.minPrice ? Number(params.minPrice) : undefined,
      maxPrice: params.maxPrice ? Number(params.maxPrice) : undefined,
      search: params.search || undefined,
      sortBy: (params.sortBy as ProductFilters["sortBy"]) || "newest",
      // sizes is serialized as comma-separated string: "S,M,L"
      sizes: sizesParam ? sizesParam.split(",").filter(Boolean) : undefined,
      style: params.style || undefined,
      color: params.color || undefined,
      occasion: params.occasion || undefined,
      material: params.material || undefined,
      fitting: params.fitting || undefined,
      onSale: params.onSale === "true" || params.onSale === "1" ? true : undefined,
      condition: params.condition || undefined,
      page: 1, // Always start at page 1 for URL-based filters
    };
  }, [searchParams, shopTaxonomy]);

  const updateURL = useCallback((filters: ProductFilters) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== "" && value !== null && value !== "All" && key !== "page") {
        if (key === "sizes" && Array.isArray(value)) {
          // Serialize sizes array as comma-separated string
          if (value.length > 0) params.set("sizes", value.join(","));
        } else if (key === "category") {
          // A category link (KEZA'S EMPIRE UG, Nov 2026): the shop's own slug,
          // not the chip's name. The page reads slugs, so a copied address
          // opens on the same chips on any phone (lib/shop/categoryLinks.ts).
          params.set(key, linkValueFor(String(value), categorySlugMap.current) ?? String(value));
        } else if (key === "subcategory") {
          params.set(key, linkValueFor(String(value), subcategorySlugMapRef.current, shopSlugMaps.subcategory) ?? String(value));
        } else if (key === "productType") {
          params.set(key, linkValueFor(String(value), productTypeSlugMapRef.current, shopSlugMaps.productType) ?? String(value));
        } else {
          params.set(key, String(value));
        }
      }
    });
    const newURL = params.toString() ? `?${params.toString()}` : "";
    router.replace(newURL, { scroll: false });
  }, [router, shopSlugMaps]);

  // Track the previous store value to detect external changes (e.g. header dropdown)
  const prevStoreRef = useRef<string | undefined>(localFilters.store);

  useEffect(() => {
    const urlFilters = getFiltersFromParams();
    setLocalFilters(urlFilters);
    setSearchQuery(urlFilters.search || "");

    // If the store changed from an external source (header dropdown), refetch immediately
    if (urlFilters.store !== prevStoreRef.current) {
      prevStoreRef.current = urlFilters.store;
      refetchProducts(urlFilters);
    }
  }, [getFiltersFromParams]);

  // Client-side product refetch — replaces products instantly without waiting for SSR
  const refetchProducts = useCallback(async (filters: ProductFilters) => {
    if (!brandId) {
      setIsLoading(false); // Bug 6 fix: guard early return so loading never gets stuck
      return;
    }
    setIsLoading(true);
    try {
      const data = await fetchQuery(api.products.shop.getShopProducts, {
        brandId: brandId as Id<"brands">,
        // Translate display names to slugs — filterOptions stores names for display
        // but Convex queries look up by slug
        category: toSlug(filters.category, categorySlugMap.current),
        subcategory: toSlug(filters.subcategory, subcategorySlugMapRef.current),
        productType: toSlug(filters.productType, productTypeSlugMapRef.current),
        search: filters.search || undefined,
        minPrice: filters.minPrice ? Number(filters.minPrice) : undefined,
        maxPrice: filters.maxPrice ? Number(filters.maxPrice) : undefined,
        storeId: filters.store && filters.store !== "All" ? filters.store as Id<"stores"> : undefined,
        // Bug 5 fix: pass sizes array (multi-select)
        sizes: filters.sizes && filters.sizes.length > 0 ? filters.sizes : undefined,
        color: filters.color && filters.color !== "All" ? filters.color : undefined,
        condition: filters.condition && filters.condition !== "All" ? filters.condition : undefined,
        sortBy: filters.sortBy || "newest",
        onSale: filters.onSale ? true : undefined,
        page: 1,
        limit: 24,
      });

      // The SAME mapping the server page uses — a car, a menu item or a
      // property listing keeps its details after a filter, not just on the
      // first paint. Menu items keep their REAL stock too: without it every
      // drink went sold out the moment a visitor filtered.
      const menuStock = await loadMenuStock(String(brandId), data.products);
      const newShopProducts: ShopProduct[] = data.products.map((p: any) =>
        toShopProduct(p, { brandId: String(brandId), industry, menuStock }),
      );

      const nextPagination = { ...data.pagination, onSaleCount: (data as any).onSaleCount ?? 0 };
      setAllProducts(newShopProducts);
      setCurrentPage(1);
      setCurrentPagination(nextPagination);
      setShopProducts(shopKey, newShopProducts, 1, nextPagination);
    } catch (error) {
      console.error("Failed to refetch products:", error);
    } finally {
      setIsLoading(false);
    }
  }, [brandId, industry, shopKey, setShopProducts, toSlug]);

  // Load more products (append to existing) - uses Convex directly
  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !currentPagination.hasNext || !brandId) return;

    setIsLoadingMore(true);
    const nextPage = currentPage + 1;

    try {
      const data = await fetchQuery(api.products.shop.getShopProducts, {
        brandId: brandId as Id<"brands">,
        // Translate names to slugs for load-more (same maps, always current via ref)
        category: toSlug(localFilters.category, categorySlugMap.current),
        subcategory: toSlug(localFilters.subcategory, subcategorySlugMapRef.current),
        productType: toSlug(localFilters.productType, productTypeSlugMapRef.current),
        search: localFilters.search || undefined,
        minPrice: localFilters.minPrice ? Number(localFilters.minPrice) : undefined,
        maxPrice: localFilters.maxPrice ? Number(localFilters.maxPrice) : undefined,
        storeId: localFilters.store && localFilters.store !== "All" ? localFilters.store as Id<"stores"> : undefined,
        sizes: localFilters.sizes && localFilters.sizes.length > 0 ? localFilters.sizes : undefined,
        color: localFilters.color && localFilters.color !== "All" ? localFilters.color : undefined,
        condition: localFilters.condition && localFilters.condition !== "All" ? localFilters.condition : undefined,
        sortBy: localFilters.sortBy || "newest",
        onSale: localFilters.onSale ? true : undefined,
        page: nextPage,
        limit: 24,
      });

      // The SAME mapping the server page uses — a car, a menu item or a
      // property listing keeps its details after a filter, not just on the
      // first paint. Page 2's drinks keep their real stock too.
      const menuStock = await loadMenuStock(String(brandId), data.products);
      const newShopProducts: ShopProduct[] = data.products.map((p: any) =>
        toShopProduct(p, { brandId: String(brandId), industry, menuStock }),
      );

      const combinedProducts = [...allProducts, ...newShopProducts];
      setAllProducts(combinedProducts);
      setCurrentPage(nextPage);
      setCurrentPagination(data.pagination);
      appendProducts(shopKey, newShopProducts, nextPage, data.pagination);
    } catch (error) {
      console.error("Failed to load more products:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, currentPagination.hasNext, currentPage, localFilters, brandId, industry, allProducts, shopKey, appendProducts, toSlug]);

  const handleFilterChange = useCallback((key: keyof ProductFilters, value: any) => {
    // Bug 6 fix: only set loading if brandId exists — prevents stuck loading state
    if (brandId) setIsLoading(true);
    let newFilters = { ...localFilters, [key]: value };

    // For array values (sizes), don't delete on empty string — check array length
    if (Array.isArray(value)) {
      if (value.length === 0) delete newFilters[key];
    } else if (value === "" || value === undefined || value === "All") {
      delete newFilters[key];
    }

    // Special handling for price - if minPrice or maxPrice changes, preserve both
    if (key === "minPrice" || key === "maxPrice") {
      if (key === "minPrice") {
        newFilters.minPrice = value;
        if (localFilters.maxPrice !== undefined) newFilters.maxPrice = localFilters.maxPrice;
      } else if (key === "maxPrice") {
        newFilters.maxPrice = value;
        if (localFilters.minPrice !== undefined) newFilters.minPrice = localFilters.minPrice;
      }
    }

    // Clear dependent filters and cascade subcategories when category changes
    if (key === "category") {
      delete newFilters.subcategory;
      delete newFilters.productType;
      // Restore product types to full initial list (don't clear — keeps section visible)
      setCurrentProductTypes(initialProductTypeNames.current);
      // Reset product type slug map when category changes
      productTypeSlugMapRef.current = {};
      if (value && value !== "All" && brandId) {
        // Resolve the correct slug from our name→slug map (fallback to value as-is)
        const categorySlug = categorySlugMap.current[value] || value.toLowerCase().replace(/\s+/g, "-");
        fetchQuery(api.products.shop.getSubcategoriesForCategory, {
          brandId: brandId as Id<"brands">,
          categorySlug,
        })
          .then((subcats) => {
            // Store both names (for display) and name→slug map (for next cascade)
            const newSlugMap: Record<string, string> = {};
            subcats.forEach((s: { name: string; slug: string }) => { newSlugMap[s.name] = s.slug; });
            // Update both the state (for handleFilterChange closure) and the ref (for refetchProducts)
            setSubcategorySlugMap(newSlugMap);
            subcategorySlugMapRef.current = newSlugMap;
            setCurrentSubcategories(["All", ...subcats.map((s: { name: string }) => s.name)]);
          })
          .catch(() => {
            setSubcategorySlugMap({});
            subcategorySlugMapRef.current = {};
            setCurrentSubcategories(["All"]);
          });
      } else {
        setSubcategorySlugMap({});
        subcategorySlugMapRef.current = {};
        setCurrentSubcategories(["All"]);
      }
    }

    // Clear product type and cascade product types when subcategory changes
    if (key === "subcategory") {
      delete newFilters.productType;
      // Keep current product types visible while fetch runs (don't hide the section)
      if (value && value !== "All" && brandId) {
        // Resolve the correct slug: prefer the cascade-built map, fallback to slug-ify the name
        const subcategorySlug = subcategorySlugMap[value] || value.toLowerCase().replace(/\s+/g, "-");
        fetchQuery(api.products.shop.getProductTypesForSubcategory, {
          brandId: brandId as Id<"brands">,
          subcategorySlug,
        })
          .then((types) => {
            // Store name→slug map for product types in the ref (used by refetchProducts)
            const newPTSlugMap: Record<string, string> = {};
            types.forEach((t: { name: string; slug: string }) => { newPTSlugMap[t.name] = t.slug; });
            productTypeSlugMapRef.current = newPTSlugMap;
            // If no types found for this subcategory, fall back to the full initial list
            if (types.length === 0) {
              setCurrentProductTypes(initialProductTypeNames.current);
            } else {
              setCurrentProductTypes(["All", ...types.map((t: { name: string }) => t.name)]);
            }
          })
          .catch(() => {
            productTypeSlugMapRef.current = {};
            setCurrentProductTypes(initialProductTypeNames.current);
          });
      } else {
        // Reset to full list when subcategory is cleared
        productTypeSlugMapRef.current = {};
        setCurrentProductTypes(initialProductTypeNames.current);
      }
    }

    newFilters.page = 1;
    setLocalFilters(newFilters);
    setCurrentPage(1);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    // Immediate filters: category, subcategory, productType, store, sizes, color, condition
    if (key === "category" || key === "subcategory" || key === "productType" || key === "store" || key === "sizes" || key === "color" || key === "condition") {
      updateURL(newFilters);
      refetchProducts(newFilters);
    } else {
      // Debounced filters: search, price, etc.
      timeoutRef.current = setTimeout(() => {
        updateURL(newFilters);
        refetchProducts(newFilters);
      }, 200);
    }
  }, [localFilters, updateURL, refetchProducts, brandId]);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (query.length >= 1 || query.length === 0) handleFilterChange("search", query || undefined);
    }, 150);
  }, [handleFilterChange]);

  const handleSearchSubmit = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    handleFilterChange("search", searchQuery || undefined);
  }, [searchQuery, handleFilterChange]);

  const handleSortChange = useCallback((sortBy: string) => handleFilterChange("sortBy", sortBy), [handleFilterChange]);
  const handleSizeSystemChange = useCallback((system: string) => { setCurrentSizeSystem(system); handleFilterChange("sizes", []); }, [handleFilterChange]);

  const handleClearFilters = useCallback(() => {
    setSearchQuery("");
    // Restore cascaded lists to the initial full state (not just empty "All")
    setCurrentSubcategories(["All", ...(initialFilterOptions.subcategories?.map((s: any) => s.name) || [])]);
    setCurrentProductTypes(initialProductTypeNames.current);
    setSubcategorySlugMap(
      Object.fromEntries(
        (initialFilterOptions.subcategories || []).map((s: any) => [s.name, s.slug])
      )
    );
    const baseFilters: ProductFilters = {
      sortBy: "newest",
      minPrice: undefined,
      maxPrice: undefined,
      // Preserve the current store selection — clearing filters shouldn't change location
      store: localFilters.store,
    };
    setLocalFilters(baseFilters);
    setCurrentPage(1);
    updateURL(baseFilters);
    refetchProducts(baseFilters);
  }, [updateURL, localFilters.store, refetchProducts, initialFilterOptions]);

  const getActiveFiltersCount = useCallback(() => {
    return Object.keys(localFilters).filter((key) => {
      if (key === "sortBy" || key === "page" || key === "store") return false;
      const val = localFilters[key as keyof ProductFilters];
      if (val === undefined || val === "" || val === "All") return false;
      // Array filters (sizes): count only when at least 1 item selected
      if (Array.isArray(val)) return val.length > 0;
      return true;
    }).length;
  }, [localFilters]);

  useEffect(() => { return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); }; }, []);

  // The conditions the shop sells (lib/config/productCondition.ts) — the chips
  // above the grid and the filter sheet show them once there are two or more.
  const conditionChips: string[] = filterOptions.conditions || [];

  return (
    <div
      className="min-h-screen"
      id="products"
      style={{
        backgroundColor: "#FBFCFD",
        scrollbarWidth: 'thin',
        scrollbarColor: '#d1d5db transparent'
      }}
    >
      <ShopControlsMinimal
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        onSearchSubmit={handleSearchSubmit}
        isLoading={isLoading}
        onFiltersOpen={() => setFiltersOpen(true)}
        activeFiltersCount={getActiveFiltersCount()}
        onClearFilters={handleClearFilters}
      />

      {/* Filter Sheet - Works for both mobile and desktop */}
      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetContent
          side="right"
          className="w-full sm:w-96 flex flex-col p-0"
        >
          <SheetHeader className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <SheetTitle>Filters</SheetTitle>
              {getActiveFiltersCount() > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { handleClearFilters(); }}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs"
                >
                  Clear All
                </Button>
              )}
            </div>
          </SheetHeader>
          <div
            className="flex-1 overflow-y-auto px-6 py-6 scrollbar-light"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#d1d5db transparent'
            }}
          >
            <ProductFiltersComponent
              filters={localFilters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              filterOptions={filterOptions}
              activeFiltersCount={getActiveFiltersCount()}
              isLoading={isLoading}
              onSizeSystemChange={handleSizeSystemChange}
              sortBy={localFilters.sortBy || "newest"}
              onSortChange={handleSortChange}
              showSortInFilters={true}
            />
          </div>
          <div className="px-6 py-4 border-t border-gray-200">
            <Button
              onClick={() => setFiltersOpen(false)}
              className="w-full bg-black text-white hover:bg-gray-800"
            >
              Show {currentPagination.total} Products
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <div className="max-w-7xl mx-auto px-6 py-6 lg:py-10">
        {beforeGrid && getActiveFiltersCount() === 0 && !searchQuery ? beforeGrid : null}
        {/* A bar night that is on and marked for the website — the menu prices by it — and the way to a table. */}
        {brandId ? (
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <TonightBanner brandId={brandId} variant="shop" className="min-w-0 flex-1" />
            <ReserveTableButton brandId={brandId} />
          </div>
        ) : null}
        {/* On sale (Venus, Oct 2026): the products with an old price crossed
            out. Then New / used (Bagz Phones, Oct 2026): one tap for the same
            phone in the condition a shopper wants, once the shop sells two. */}
        {((currentPagination.onSaleCount ?? 0) > 0 || localFilters.onSale || conditionChips.length > 1) && (
          <div className="mb-5 flex flex-wrap items-center gap-2">
            {((currentPagination.onSaleCount ?? 0) > 0 || localFilters.onSale) && (
              <button
                type="button"
                onClick={() => handleFilterChange("onSale", localFilters.onSale ? undefined : true)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
                  localFilters.onSale ? "border-red-500 bg-red-500 text-white" : "border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
                }`}
              >
                <span aria-hidden>%</span> On sale{currentPagination.onSaleCount ? ` · ${currentPagination.onSaleCount}` : ""}
                {localFilters.onSale ? <span aria-hidden className="ml-1">✕</span> : null}
              </button>
            )}
            {conditionChips.length > 1 &&
              conditionChips.map((label) => {
                const on = sameCondition(localFilters.condition, label);
                return (
                  <button
                    key={label}
                    type="button"
                    aria-pressed={on}
                    onClick={() => handleFilterChange("condition", on ? undefined : label)}
                    className={`inline-flex max-w-full items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
                      on ? "border-black bg-black text-white" : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span className="truncate">{label}</span>
                    {on ? <span aria-hidden className="ml-1">✕</span> : null}
                  </button>
                );
              })}
          </div>
        )}
        <ProductGrid
          products={allProducts}
          pagination={currentPagination}
          onLoadMore={handleLoadMore}
          onClearFilters={handleClearFilters}
          isLoadingMore={isLoadingMore}
          hasAnyProducts={currentPagination.total > 0 || allProducts.length > 0}
          activeFiltersCount={getActiveFiltersCount()}
          gridCols={4}
          industry={industry}
        />
      </div>
    </div>
  );
}
